/**
 * React Query data sanitizer to prevent Sentry serialization errors
 * Wraps query data to remove undefined date values before they reach monitoring tools
 */

import { deepSanitizeDates } from './dateUtils';

import { logger } from '@/utils/logger';

/**
 * Sanitize React Query data to prevent undefined date serialization issues
 */
export function sanitizeQueryData<T>(data: T): T {
  if (!data) {
    return data;
  }

  try {
    // Deep sanitize the data to remove undefined values that cause Sentry issues
    return deepSanitizeDates(data);
  } catch (error) {
    logger.warn('Failed to sanitize query data:', error);
    return data; // Return original data if sanitization fails
  }
}

/**
 * Wrapper for React Query onSuccess callback that sanitizes data
 */
export function createSanitizedSuccessHandler<T>(onSuccess?: (data: T) => void): (data: T) => void {
  return (data: T) => {
    if (onSuccess) {
      try {
        const sanitizedData = sanitizeQueryData(data);
        onSuccess(sanitizedData);
      } catch (error) {
        logger.warn('Error in sanitized success handler:', error);
        onSuccess(data); // Fallback to original data
      }
    }
  };
}

/**
 * Sanitize mutation variables before they're processed
 */
export function sanitizeMutationVariables<T>(variables: T): T {
  return sanitizeQueryData(variables);
}
